/**
 * Task #1237 — Master FE hook: snapshot storage cluster-wide aggregato.
 * Polling 90s su /api/master/storage/db/tenants → ritorna lista tenant
 * con quota DB + flags hard-block. Per il widget header mostra peggior tenant.
 */
import { useEffect, useState, useCallback } from 'react';
import { apiClient } from '@/lib/api';
import type { MasterTenantDbQuotaRow } from '@easyflow/shared';

// Alias di compatibilità per i consumer esistenti (DTO condiviso = SoT).
export type MasterTenantQuotaRow = MasterTenantDbQuotaRow;


const POLL_MS = 90_000;

export function useMasterStorageQuota(): {
  rows: MasterTenantQuotaRow[];
  loading: boolean;
  worst: MasterTenantQuotaRow | null;
  blockedCount: number;
  refresh: () => Promise<void>;
} {
  const [rows, setRows] = useState<MasterTenantQuotaRow[]>([]);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const r = await apiClient.get<MasterTenantDbQuotaRow[]>('/master/storage/db/tenants');
      setRows(Array.isArray(r.data) ? r.data : []);
    } catch {
      // best-effort
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
    const id = setInterval(() => { void refresh(); }, POLL_MS);
    return () => clearInterval(id);
  }, [refresh]);

  const order: Record<string, number> = { blocked: 6, grace: 5, critical: 4, warning: 3, info: 2, ok: 1 };
  const worst = rows.length === 0
    ? null
    : [...rows].sort((a, b) => (order[b.status] || 0) - (order[a.status] || 0))[0]!;
  const blockedCount = rows.filter((r) => r.status === 'blocked' || r.dbHardBlockedSince || r.r2HardBlockedSince).length;

  return { rows, loading, worst, blockedCount, refresh };
}

export function formatBytes(b: number): string {
  if (!b) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(b) / Math.log(k));
  return `${parseFloat((b / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
}
